import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Logo from "@/components/Logo";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { ArrowRight, Building2, Crown, CheckCircle2, Loader2, Shield, Globe, Database } from "lucide-react";

/**
 * Enterprise consultation form — no checkout, no Stripe.
 *
 * Posts to /api/billing/enterprise/inquiry which persists the lead and
 * (best-effort) fires an email to the configured admin inbox so the
 * founder hears about it immediately.
 *
 * Why a form (not Calendly): the spec calls for a "consultation
 * workflow", and a form gives us structured lead data (org size, teams,
 * athletes) that Calendly doesn't capture cleanly.
 */
export default function EnterpriseContact() {
  const navigate = useNavigate();
  const [form, setForm] = useState({
    organization: "", contact_name: "", email: "", phone: "",
    role: "", teams_count: "", athletes_count: "", interest: "",
  });
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState("");

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError("");
    try {
      await api.post("/billing/enterprise/inquiry", {
        ...form,
        teams_count: form.teams_count ? Number(form.teams_count) : null,
        athletes_count: form.athletes_count ? Number(form.athletes_count) : null,
        source_url: window.location.href,
      });
      setSubmitted(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally { setBusy(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--ink-1)" }} data-testid="enterprise-thanks">
        <PageHeader />
        <main className="flex-1 max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 rounded-full" style={{ backgroundColor: "rgba(255,215,0,0.15)" }}>
            <CheckCircle2 className="h-8 w-8" style={{ color: "#FFD700" }} />
          </div>
          <h1 className="font-display font-black text-4xl tracking-tighter text-white mt-6">
            We received your request.
          </h1>
          <p className="text-white/65 mt-4 leading-relaxed">
            Our team will reach out within one business day to schedule your Enterprise consultation. In the meantime, you can keep exploring the platform.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button onClick={() => navigate("/")} variant="outline" className="border-white/20 text-white bg-white/5 hover:bg-white/10">Back to home</Button>
            <Link to="/pricing">
              <Button className="text-white red-glow font-bold" style={{ backgroundColor: "var(--brand-secondary)" }}>
                View other plans <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: "var(--ink-1)" }} data-testid="enterprise-contact-page">
      <PageHeader />
      <main className="flex-1 max-w-6xl mx-auto px-6 py-16 grid lg:grid-cols-2 gap-12">
        {/* Left rail — pitch */}
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-white/15 px-3 py-1.5 text-[10px] tracking-[0.3em] uppercase font-bold text-white/70">
            <Crown className="h-3 w-3" style={{ color: "#FFD700" }} /> Enterprise · Consultation
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl tracking-tighter text-white mt-5 leading-[0.95]">
            Build an<br /><span style={{ color: "#FFD700" }}>Organization.</span>
          </h1>
          <p className="text-white/65 mt-5 leading-relaxed max-w-md">
            Enterprise customers operate multi-campus organizations, MLB affiliates, governing bodies, or national academies. Pricing is custom because configuration is custom — let&apos;s talk.
          </p>
          <ul className="mt-8 space-y-3">
            {[
              { Icon: Database, t: "Unlimited athletes, coaches, teams + organizational hierarchy" },
              { Icon: Shield,   t: "Custom AI models, enterprise data pipelines, dedicated rate limits" },
              { Icon: Globe,    t: "White-label branding, custom domains, executive dashboards" },
            ].map(({ Icon, t }, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-white/80">
                <Icon className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "#FFD700" }} />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* Right rail — form */}
        <form onSubmit={submit} className="rounded-2xl ink-card p-6 md:p-7 space-y-4" data-testid="enterprise-form">
          <div>
            <Label className="text-white/70">Organization *</Label>
            <Input required value={form.organization} onChange={upd("organization")} placeholder="Atlanta Braves · Stanford Baseball · USA Baseball" className="mt-1.5 bg-[var(--ink-2)] border-[var(--ink-border)] text-white" data-testid="ent-organization" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white/70">Your name *</Label>
              <Input required value={form.contact_name} onChange={upd("contact_name")} className="mt-1.5 bg-[var(--ink-2)] border-[var(--ink-border)] text-white" data-testid="ent-contact-name" />
            </div>
            <div>
              <Label className="text-white/70">Role</Label>
              <Input value={form.role} onChange={upd("role")} placeholder="Director of Performance" className="mt-1.5 bg-[var(--ink-2)] border-[var(--ink-border)] text-white" data-testid="ent-role" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white/70">Email *</Label>
              <Input required type="email" value={form.email} onChange={upd("email")} className="mt-1.5 bg-[var(--ink-2)] border-[var(--ink-border)] text-white" data-testid="ent-email" />
            </div>
            <div>
              <Label className="text-white/70">Phone</Label>
              <Input value={form.phone} onChange={upd("phone")} className="mt-1.5 bg-[var(--ink-2)] border-[var(--ink-border)] text-white" data-testid="ent-phone" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-white/70"># Teams</Label>
              <Input type="number" min="0" value={form.teams_count} onChange={upd("teams_count")} placeholder="e.g. 6" className="mt-1.5 bg-[var(--ink-2)] border-[var(--ink-border)] text-white" data-testid="ent-teams" />
            </div>
            <div>
              <Label className="text-white/70"># Athletes</Label>
              <Input type="number" min="0" value={form.athletes_count} onChange={upd("athletes_count")} placeholder="e.g. 240" className="mt-1.5 bg-[var(--ink-2)] border-[var(--ink-border)] text-white" data-testid="ent-athletes" />
            </div>
          </div>
          <div>
            <Label className="text-white/70">What are you trying to solve?</Label>
            <Textarea value={form.interest} onChange={upd("interest")} rows={4} placeholder="Integrations needed, white-label requirements, deployment timeline…" className="mt-1.5 bg-[var(--ink-2)] border-[var(--ink-border)] text-white" data-testid="ent-interest" />
          </div>
          {error && <div className="text-sm text-red-400" data-testid="ent-error">{error}</div>}
          <Button type="submit" disabled={busy} className="w-full text-[var(--ink-0)] font-bold rounded-full py-5" style={{ backgroundColor: "#FFD700" }} data-testid="ent-submit">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Request consultation <ArrowRight className="h-4 w-4 ml-2" /></>}
          </Button>
          <p className="text-[10px] text-white/40 text-center">We typically respond within one business day.</p>
        </form>
      </main>
      <Footer />
    </div>
  );
}

function PageHeader() {
  return (
    <header className="sticky top-0 z-30 backdrop-blur-xl border-b border-[var(--ink-border)]" style={{ backgroundColor: "rgba(10,20,36,0.85)" }}>
      <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
        <Logo size="md" variant="lockup" linkTo="/" dark />
        <nav className="flex items-center gap-5 text-sm text-white/70">
          <Link to="/pricing" className="hover:text-white">Pricing</Link>
          <Link to="/" className="hover:text-white">Home</Link>
        </nav>
      </div>
    </header>
  );
}
